import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

interface TimestampProps {
  date: string | number;
  'data-chromatic'?: string;
  format?:
    | 'minimal'
    | 'short'
    | 'default'
    | 'future'
    | ((date: moment.Moment) => string);
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const nextUpdateTimeout = (momentDate: moment.Moment) => {
  const diff = moment.duration(moment().valueOf() - momentDate.valueOf());

  // moment's `fromNow` rounds up to `an hour ago` for 45+ minutes
  if (Math.abs(diff.asMinutes()) <= 45) {
    return 60 * 1000;
  }

  if (Math.abs(diff.asHours()) <= 24) {
    return 30 * 60 * 1000;
  }

  return -1;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const fromNow = (past: moment.Moment) => {
  const diff = moment.duration(moment().valueOf() - past.valueOf());
  // Avoid "a few seconds ago" which can be too long for some UIs
  if (diff.asSeconds() < 60) return 'just now';
  // Otherwise longest string is 14 characters (xx minutes ago)
  if (diff.asYears() < 1) return past.fromNow();
  // Beyond a year, relative is not granular enough
  return past.format('MMM D, YYYY');
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const fromNowShort = (past: moment.Moment) => {
  const diff = moment.duration(moment().valueOf() - past.valueOf());
  if (diff.asSeconds() < 60) return 'just now';
  // Same intervals as fromNow https://momentjs.com/docs/#/displaying/fromnow/
  if (diff.asMinutes() < 45) return `${Math.round(diff.asMinutes())}min ago`;
  if (diff.asHours() < 22) return `${Math.round(diff.asHours())}hr ago`;
  if (diff.asDays() < 26) return `${Math.round(diff.asDays())}d ago`;
  if (diff.asMonths() < 11) return `${Math.round(diff.asMonths())}mo ago`;
  const format = moment.localeData().longDateFormat('l').replace('YYYY', 'YY');
  return past.format(format);
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const fromNowMinimal = (past: moment.Moment) => {
  // Up to 3 characters, except '11mo' which has 2 thin characters
  const diff = moment.duration(moment().valueOf() - past.valueOf());

  // Avoid minutes that could be confused with months ex 11m
  if (diff.asMinutes() < 15) return 'now';
  if (diff.asMinutes() < 45) return `${Math.round(diff.asMinutes())}m`;
  if (diff.asHours() < 22) return `${Math.round(diff.asHours())}h`;
  if (diff.asDays() < 26) return `${Math.round(diff.asDays())}d`;

  // Specifically avoid '10mo'. 11mo is okay since there's two thin characters
  if (diff.asMonths() < 9.5) return `${Math.round(diff.asMonths())}mo`;
  if (diff.asMonths() < 12) return `11mo`;
  // Absolute date too long, cap at 3 characters
  return `${Math.min(99, Math.round(diff.asYears()))}y`;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const nowUntil = (future: moment.Moment) => {
  if (future.diff(moment(), 'days') <= 1) {
    return 'today';
  }
  return future.fromNow();
};

const Timestamp = ({ date, format = 'default', ...rest }: TimestampProps) => {
  const formatter = useMemo(() => {
    if (typeof format === 'function') return format;
    if (format === 'short') return fromNowShort;
    if (format === 'future') return nowUntil;
    if (format === 'minimal') return fromNowMinimal;
    return fromNow;
  }, [format]);

  const momentDate = useMemo(() => moment(date), [date]);
  const [relativeTime, setRelativeTime] = useState(() => formatter(momentDate));

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const innerTick = () => {
      setRelativeTime(formatter(momentDate));
      const ms = nextUpdateTimeout(momentDate);
      timeout = ms > 0 ? setTimeout(innerTick, ms) : null;
    };

    innerTick();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [formatter, momentDate]);

  return (
    <time dateTime={momentDate.toISOString()} {...rest}>
      {relativeTime}
    </time>
  );
};

export default Timestamp;
